import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

/** Fine-grained local resume state. Keyed `${userId}:${hazardId}`. */
export interface TrainingProgress {
  /** The server session the completed chapters were recorded in; a different session re-walks them. */
  sessionId?: string;
  chapterIdx: number;
  scrollPct: number;
  /** Accumulated dwell seconds per chapter id. */
  dwellByChapter: Record<string, number>;
  /** Required widget ids the worker has interacted with, per chapter id. */
  widgetsByChapter: Record<string, string[]>;
  completedChapterIds: string[];
  /** Draft quiz answers (questionId → choice), so a reload mid-quiz survives. */
  quizDraft: Record<string, unknown>;
  updatedAt: number;
}

interface ProgressDB extends DBSchema {
  progress: { key: string; value: TrainingProgress };
}

const DB_NAME = 'fixit-bozp';
const STORE = 'progress';

let dbPromise: Promise<IDBPDatabase<ProgressDB>> | null = null;

function db(): Promise<IDBPDatabase<ProgressDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ProgressDB>(DB_NAME, 1, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE)) {
          database.createObjectStore(STORE);
        }
      },
    });
  }
  return dbPromise;
}

function key(userId: string, hazardId: string): string {
  return `${userId}:${hazardId}`;
}

export async function loadProgress(
  userId: string,
  hazardId: string,
): Promise<TrainingProgress | null> {
  const value = await (await db()).get(STORE, key(userId, hazardId));
  return value ?? null;
}

export async function saveProgress(
  userId: string,
  hazardId: string,
  progress: TrainingProgress,
): Promise<void> {
  await (await db()).put(STORE, progress, key(userId, hazardId));
}

export async function clearProgress(userId: string, hazardId: string): Promise<void> {
  await (await db()).delete(STORE, key(userId, hazardId));
}
